import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, X, FileDown, Building2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

export default function PublicBudget() {
    const { token } = useParams();
    const [b, setB] = useState(null);
    const [err, setErr] = useState("");
    const [decision, setDecision] = useState(null); // 'schvaleno' | 'zamitnuto' | null
    const [form, setForm] = useState({ client_name: "", note: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/public/budgets/${token}`);
            setB(data);
        } catch (e) {
            setErr(e.response?.data?.detail || "Rozpočet nenalezen");
        }
    };
    useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async (which) => {
        if (!form.client_name.trim()) { setErr("Vyplňte své jméno"); return; }
        setBusy(true); setErr("");
        try {
            await axios.post(`${API_BASE}/public/budgets/${token}/decide`, {
                client_name: form.client_name, note: form.note, decision: which,
            });
            await load();
            setDecision(null);
        } catch (e) {
            setErr(e.response?.data?.detail || "Akce selhala");
        } finally { setBusy(false); }
    };

    if (err && !b) return <CenterMsg title="Rozpočet nenalezen" msg={err} />;
    if (!b) return <CenterMsg title="Načítání…" />;

    const c = b.company || {};
    const isDecided = b.status === "schvaleno" || b.status === "zamitnuto";
    const logoUrl = c.logo_path ? `${API_BASE}/public/budgets/${token}/logo` : null;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="public-budget-page">
            {/* Brand header */}
            <div className="bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img src="/ceramixa-logo.png" alt={c.name} className="h-14 w-auto" />
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow text-orange-400">Rozpočet</div>
                        <div className="font-mono text-sm text-slate-300">{b.number}</div>
                    </div>
                </div>
                <div className="h-1 gold-rule"></div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-8">
                {/* Status banner */}
                {isDecided && (
                    <div className={`mb-6 border-l-4 px-5 py-4 ${b.status === "schvaleno" ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50"}`}>
                        <div className="flex items-center gap-2 font-semibold">
                            {b.status === "schvaleno" ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <XCircle className="w-5 h-5 text-red-700" />}
                            <span className={b.status === "schvaleno" ? "text-green-800" : "text-red-800"}>
                                {b.status === "schvaleno" ? "Rozpočet byl schválen" : "Rozpočet byl zamítnut"}
                            </span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                            {b.approved_by_name} · {b.approved_at && new Date(b.approved_at).toLocaleString("cs-CZ")}
                        </div>
                        {b.approval_note && <div className="text-sm text-slate-600 mt-1 italic">„{b.approval_note}"</div>}
                    </div>
                )}

                {/* Hero */}
                <div className="mb-8">
                    <div className="label-eyebrow mb-2">Cenová nabídka pro vás</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 mb-3">{b.title}</h1>
                    {b.valid_until && <div className="text-sm text-slate-600">Platnost do <b>{b.valid_until}</b></div>}
                </div>

                {/* Info blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <InfoBlock label="Dodavatel">
                        <div className="font-semibold text-slate-900">{c.name}</div>
                        <div className="text-sm text-slate-600">{c.address}</div>
                        <div className="text-sm text-slate-600">{c.zip} {c.city}</div>
                        <div className="text-sm text-slate-600 mt-1">IČO: {c.ico || "—"} · DIČ: {c.dic || "—"}</div>
                        <div className="text-sm text-slate-600 mt-1">{c.phone} · {c.email}</div>
                    </InfoBlock>
                    <InfoBlock label="Odběratel">
                        <div className="font-semibold text-slate-900">{b.client_name || "—"}</div>
                        <div className="text-sm text-slate-600">{b.client_address}</div>
                        {b.site_address && (
                            <div className="text-sm text-slate-600 mt-2">
                                <span className="label-eyebrow block mb-0.5">Místo stavby</span>
                                {b.site_address}
                            </div>
                        )}
                    </InfoBlock>
                </div>

                {/* Items */}
                <div className="bg-white border border-slate-200 mb-6">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Položky rozpočtu</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                <th className="px-4 py-3 w-8">#</th>
                                <th className="px-4 py-3">Položka</th>
                                <th className="px-4 py-3 text-right w-24">Množství</th>
                                <th className="px-4 py-3 w-16">MJ</th>
                                {b.pdf_split_prices && <th className="px-4 py-3 text-right w-24">Práce/MJ</th>}
                                {b.pdf_split_prices && <th className="px-4 py-3 text-right w-24">Materiál/MJ</th>}
                                <th className="px-4 py-3 text-right w-28">Cena/MJ</th>
                                <th className="px-4 py-3 text-right w-32">Celkem</th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {b.items.map((it, idx) => (
                                <tr key={it.id || idx} className={`border-b border-slate-100 ${idx % 2 ? "bg-slate-50/40" : ""}`}>
                                    <td className="px-4 py-3 text-center text-slate-400">{idx + 1}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{it.name}</div>
                                        {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-right">{Number(it.quantity).toFixed(2).replace(".", ",")}</td>
                                    <td className="px-4 py-3 text-slate-600">{it.unit}</td>
                                    {b.pdf_split_prices && <td className="px-4 py-3 text-right text-slate-600">{fmt(it.labor_price ?? it.unit_price)}</td>}
                                    {b.pdf_split_prices && <td className="px-4 py-3 text-right text-slate-600">{fmt(it.material_price ?? 0)}</td>}
                                    <td className="px-4 py-3 text-right">{fmt(it.unit_price)}</td>
                                    <td className="px-4 py-3 text-right font-semibold">{fmt(it.total || it.quantity * it.unit_price)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end mb-8">
                    <div className="bg-white border border-slate-200 w-full md:w-96">
                        <Row label="Mezisoučet (bez DPH)" value={fmt(b.subtotal)} />
                        <Row label={`DPH ${b.vat_rate}%`} value={fmt(b.vat)} />
                        <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
                            <span className="font-display font-bold text-sm uppercase tracking-wider">Celkem</span>
                            <span className="font-display text-2xl font-black tabular-data">{fmt(b.total)}</span>
                        </div>
                    </div>
                </div>

                {b.notes && (
                    <div className="bg-white border border-slate-200 p-5 mb-6">
                        <div className="label-eyebrow mb-2">Poznámka</div>
                        <div className="text-sm text-slate-700 whitespace-pre-wrap">{b.notes}</div>
                    </div>
                )}

                {/* Approval */}
                {!isDecided && (
                    <div className="bg-white border-2 border-slate-900 sharp-shadow">
                        <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-3">
                            <ShieldCheck className="w-5 h-5 text-orange-600" />
                            <div className="font-display font-bold uppercase tracking-wider text-sm">Vaše rozhodnutí</div>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600">Po schválení dostane <b>{c.name}</b> okamžitou notifikaci. Rozhodnutí je pak závazné.</p>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Vaše jméno *</label>
                                <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="public-client-name"
                                       className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Poznámka (volitelné)</label>
                                <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="public-note"
                                          className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm resize-none" />
                            </div>
                            {err && <div className="border-l-4 border-red-600 bg-red-50 px-4 py-2 text-sm text-red-700">{err}</div>}
                            <div className="flex gap-3">
                                <button onClick={() => submit("schvaleno")} disabled={busy} data-testid="public-approve"
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                                    <Check className="w-4 h-4" /> Schválit rozpočet
                                </button>
                                <button onClick={() => submit("zamitnuto")} disabled={busy} data-testid="public-reject"
                                        className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                                    <X className="w-4 h-4" /> Zamítnout
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-12 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between text-sm text-slate-500">
                    <div>{c.name} · IČO {c.ico || "—"}</div>
                    <a href={`${API_BASE}/public/budgets/${token}/pdf`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 text-slate-700 hover:text-orange-600">
                        <FileDown className="w-4 h-4" /> Stáhnout PDF
                    </a>
                </div>
            </div>
        </div>
    );
}

function InfoBlock({ label, children }) {
    return (
        <div className="bg-white border border-slate-200 p-5">
            <div className="label-eyebrow mb-3">{label}</div>
            {children}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between px-6 py-2.5 border-b border-slate-100 text-sm">
            <span className="text-slate-600">{label}</span>
            <span className="font-semibold tabular-data">{value}</span>
        </div>
    );
}

function CenterMsg({ title, msg }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="bg-white border border-slate-200 p-8 max-w-md text-center">
                <div className="font-display text-2xl font-black text-slate-900 mb-2">{title}</div>
                {msg && <div className="text-sm text-slate-600">{msg}</div>}
            </div>
        </div>
    );
}
